
import { ColumnDefinition } from '@/components/CRUD/DynamicDataTable';
import { ColumnMeta, RowData } from '@/types/layout';
import moment from 'moment';
import { Toast } from 'primereact/toast';
import { useEffect, useRef, useState } from 'react';

// Function to save the any in the local storage
export const saveToLocalStorage = (clave: string, data: any) => {
  // Si `data` es un objeto o un array, conviértelo en JSON
  const dataToStore = typeof data === 'object' ? JSON.stringify(data) : data;
  localStorage.setItem(clave, dataToStore);
};

export const getToLocalStorage = (clave: string) => {
  if (typeof window !== 'undefined' && window.localStorage) {
      const storedData = localStorage.getItem(clave);
      try {
          return storedData ? JSON.parse(storedData) : null;
      } catch (e) {
          return storedData;
      }
  } else {
      return null; // Otra opción, si lo prefieres, podría ser lanzar una excepción.
  }
};

 export const transformBooleanFieldsToServerFormat = (data: RowData, columnas: ColumnDefinition[]): RowData => {
    const transformedData = { ...data };
    columnas.forEach((columna) => {
      if (columna.type === 'BooleanField' && columna.name in transformedData) {
        if (transformedData[columna.name] === 'Sí' || transformedData[columna.name] === 'No') {
          transformedData[columna.name] = transformedData[columna.name] === 'Sí' ? true : false;
        }
      }
    });
    return transformedData;
  };


export const isModuloActivo = (modulo: string) => {
    const modulosActivos = getToLocalStorage('modulos-activos');
    return modulosActivos?.includes(modulo);
}

export const isParametroCarteraActivo = () => {
    const parametrosActivos = getToLocalStorage('parametros-activos');
    return parametrosActivos?.CLIENTECARTERA;
}

export const showMessage = (
  messageRef: React.MutableRefObject<Toast | null>,
  severity: 'success' | 'error',
  summary: string,
  detail: string,
  life: number = 3000
) => {
  messageRef.current?.show({ severity, summary, detail, life });
};

const useMaxHeight = () => {
  const refs = useRef<HTMLDivElement[]>([]);
  const [maxHeight, setMaxHeight] = useState<number>(0);
  const [maxWidth, setMaxWidth] = useState<number>(0);

  useEffect(() => {
    const calculateMaxDimensions = () => {
      const heights = refs.current.map(ref => ref?.offsetHeight || 0);
      const widths = refs.current.map(ref => ref?.offsetWidth || 0);
      setMaxHeight(Math.max(...heights));
      setMaxWidth(Math.max(...widths));
    };

    calculateMaxDimensions();

    // Opcional: recalcular en caso de cambios de tamaño de ventana
    window.addEventListener('resize', calculateMaxDimensions);
    return () => window.removeEventListener('resize', calculateMaxDimensions);
  }, []); // Elimina refs.current de las dependencias

  return { refs, maxHeight, maxWidth };
};


export default useMaxHeight;

// Función para convertir cadena "YYYY-MM-DD" a objeto Date usando moment
export const stringToDate = (dateString: string) => {
  if (!dateString) return null;
  return moment(dateString, 'YYYY-MM-DD').toDate(); // Convertir cadena a Date
};

export const stringToTimeDate = (dateString: string) => {
  if (!dateString) return null;
  return moment(dateString, 'YYYY-MM-DD HH:mm:ss').toDate(); // Convertir cadena a Date
};

// Función para convertir objeto Date a cadena "YYYY-MM-DD" usando moment
export const dateToString = (date: Date) => {
  return moment(date).format('YYYY-MM-DD'); // Convertir Date a cadena en formato "YYYY-MM-DD"
};

export const dateTimeToString = (date: Date) => {
  return moment(date).format('YYYY-MM-DD HH:mm:ss'); // Convertir Date a cadena en formato "YYYY-MM-DD HH:mm:ss"
};


export const sumarDiasToDate = (fecha: Date | string, dias: number): Date => {
  const d = new Date(fecha);
  d.setDate(d.getDate() + Number(dias || 0));
  return d;
};

export const exportPdf = (exportColumns:any, options:any, fileName:string) => {
  import('jspdf').then((jsPDF) => {
      import('jspdf-autotable').then(() => {
          const doc = new jsPDF.default('p', 'mm');

          (doc as any).autoTable({
            head: [exportColumns.map((col: ColumnMeta) => col.header)],
            body: options.map((item: any) => exportColumns.map((col: ColumnMeta) => item[col.field])),
            styles: { fontSize: 10 },
            columnStyles: { 0: { cellWidth: 'wrap' } },
          });
          doc.save(fileName.replace(/\s+/g, "_").toLowerCase() + '.pdf');
      });
  });
};

export const exportExcel = (cols:any, options:any, fileName:string) => {
  import('xlsx').then((xlsx) => {
      // Filtrar las columnas necesarias
      const filteredFacturas = options.map((option:any) =>
          cols.reduce((acc: { [key: string]: any }, col:any) => {
              acc[col.header] = (option as any)[col.field];
              return acc;
          }, {})
      );

      // Crear la hoja de cálculo con las columnas filtradas
      const worksheet = xlsx.utils.json_to_sheet(filteredFacturas);
      const workbook = { Sheets: { data: worksheet }, SheetNames: ['data'] };
      const excelBuffer = xlsx.write(workbook, {
          bookType: 'xlsx',
          type: 'array'
      });

      saveAsExcelFile(excelBuffer, fileName.replace(/\s+/g, "_").toLowerCase());
  });
};

const saveAsExcelFile = (buffer:any, fileName:string) => {
  import('file-saver').then((module) => {
      if (module && module.default) {
          let EXCEL_TYPE = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8';
          let EXCEL_EXTENSION = '.xlsx';
          const data = new Blob([buffer], {
              type: EXCEL_TYPE
          });

          module.default.saveAs(data, fileName + '_export_' + new Date().getTime() + EXCEL_EXTENSION);
      }
  });
};

export const extractBase64 = (v?: string): string => {
    if (!v) return "";
    return v.includes(",") ? v.split(",")[1] : v;
  };